// Globals
import { debounce } from 'lodash';
import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Components
import { Form, FormItem, Modal } from '@/components';
import { StyledButton, StyledButtons, TextEditorContainer } from './TextEditorStyles';
import { Input } from '@/ui';

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'code',
  'image',
];

interface OnChangeHandler {
  (html: string, plainText: string): void;
}

type Props = {
  value?: string;
  placeholder?: string;
  onChange?: OnChangeHandler;
  enableImageUpload?: boolean;
  readOnly?: boolean;
  isSmall?: boolean;
};

export const TextEditor = React.forwardRef<ReactQuill | null, Props>(
  ({ value, onChange, placeholder, enableImageUpload = false, readOnly, isSmall }, ref) => {
    const { t } = useTranslation();
    const prevValue = useRef<string>('');
    const quillRef = useRef<ReactQuill | null>(null);

    const [selectedRange, setSelectedRange] = useState<{
      range: number;
      url: string;
    } | null>(null);

    const debouncedUpdate = useRef(
      debounce((html: string, plainText: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;

        const links = div.querySelectorAll('a');
        links.forEach((link) => {
          link.setAttribute('rel', 'nofollow');
          link.setAttribute('target', '_blank');
        });

        if (div.innerHTML && onChange) {
          prevValue.current = div.innerHTML;
          onChange(div.innerHTML, plainText);
        }
      }, 300),
    ).current;

    const handleChange = (_: string, delta: any, __: string, editor: any) => {
      const plainText = editor.getText();
      const html = editor.getHTML();

      if (html !== prevValue.current && onChange) {
        prevValue.current = html;
        debouncedUpdate(html, plainText);
      }
    };

    useImperativeHandle(ref, () => quillRef.current!);

    const modules = useMemo(() => {
      const baseToolbar = [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['link', 'clean'],
      ];

      if (enableImageUpload) {
        baseToolbar.push(['image']);
      }

      return {
        toolbar: {
          container: baseToolbar,
        },
      };
    }, [enableImageUpload]);

    const onFormFinish = (values: any) => {
      const editor = quillRef?.current?.getEditor();
      if (!selectedRange || !editor) return;
      setSelectedRange(null);
      const img = document.createElement('img');
      img.src = selectedRange.url;
      img.alt = values?.image;
      editor.clipboard.dangerouslyPasteHTML(selectedRange.range, img.outerHTML);
    };

    return (
      <TextEditorContainer $isSmall={isSmall}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          modules={modules}
          formats={formats}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
        />
        <Modal title={t('insert_image_alt_title')} visible={!!selectedRange} onCancel={() => setSelectedRange(null)}>
          <Form onFinish={onFormFinish}>
            <FormItem name="image">
              <Input />
            </FormItem>
            <StyledButtons>
              <StyledButton type="primary" htmlType="submit">
                {t('save')}
              </StyledButton>
            </StyledButtons>
          </Form>
        </Modal>
      </TextEditorContainer>
    );
  },
);

TextEditor.displayName = 'CustomTextEditor';
